import React, { useState } from "react";
import { Check, ShoppingBag, AlertCircle, Sparkles, Calendar } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useLocation } from "react-router-dom";
import FloatingMacarons from "../components/FloatingMacarons";
import { ORDERS_CLOSED, ORDERS_RETURN_DATE } from "../lib/menuData";
import macaronStrawberry from "../assets/macaron-strawberry.png";
import macaronVanilla    from "../assets/macaron-vanilla.png";
import macaronEspresso   from "../assets/macaron-chocolate.png";
import macaronToastedCoconut from "../assets/macaron-toasted-coconut.png";

// ─── Promo discounts scaled by box size (set PROMO_ACTIVE = true to enable) ───
const PROMO_ACTIVE = false;
const LAUNCH_DISCOUNTS = {
  6:  2.00,
  12: 4.00,
};

// ─── ALL flavors ───────────────────────────────────────────────
const ALL_FLAVORS = [
  { id: "vanilla",         name: "Vanilla",        image: macaronVanilla    },
  { id: "strawberry",      name: "Strawberry",     image: macaronStrawberry },
  { id: "espresso",        name: "Espresso",       image: macaronEspresso   },
  { id: "toasted-coconut", name: "Toasted Coconut", image: macaronToastedCoconut },
  // { id: "lemon",      name: "Lemon",      image: macaronLemon,      comingSoon: true },
  // { id: "peach",      name: "Peach",      image: macaronPeach,      comingSoon: true },
  // { id: "peppermint", name: "Peppermint", image: macaronPeppermint, comingSoon: true },
];

// ─── THIS WEEK'S flavors — update every Monday ────────────────
const WEEKLY_FLAVOR_IDS = ["vanilla", "strawberry", "espresso", "toasted-coconut"];
const WEEKLY_FLAVORS = ALL_FLAVORS.filter((f) => WEEKLY_FLAVOR_IDS.includes(f.id));

// ─── Orders closed — managed in src/lib/menuData.js ──────────

// ─── Quantity options ──────────────────────────────────────────
const QTY_OPTIONS = [
  { label: "Half Dozen (6)",  count: 6,  price: 15.00 },
  { label: "One Dozen (12)",  count: 12, price: 30.00 },
];

const HERO_MACARONS = [
  { top: "15%", left: "2%",  delay: 0.0, size: 54 },
  { top: "50%", left: "8%",  delay: 1.2, size: 46 },
  { top: "80%", left: "2%",  delay: 2.4, size: 50 },
  { top: "15%", left: "84%", delay: 0.6, size: 50 },
  { top: "50%", left: "92%", delay: 1.8, size: 44 },
  { top: "80%", left: "84%", delay: 3.0, size: 52 },
];

const CONTENT_MACARONS = [
  { top: "3%",  left: "8%",  delay: 0.2, size: 44 },
  { top: "14%", left: "2%",  delay: 1.5, size: 34 },
  { top: "25%", left: "8%",  delay: 0.8, size: 50 },
  { top: "36%", left: "2%",  delay: 2.0, size: 36 },
  { top: "47%", left: "8%",  delay: 0.4, size: 44 },
  { top: "58%", left: "2%",  delay: 1.3, size: 34 },
  { top: "69%", left: "8%",  delay: 0.9, size: 48 },
  { top: "80%", left: "2%",  delay: 1.7, size: 36 },
  { top: "91%", left: "8%",  delay: 0.5, size: 42 },
  { top: "3%",  left: "92%", delay: 0.6, size: 40 },
  { top: "14%", left: "84%", delay: 1.9, size: 50 },
  { top: "25%", left: "92%", delay: 0.1, size: 34 },
  { top: "36%", left: "84%", delay: 2.3, size: 46 },
  { top: "47%", left: "92%", delay: 0.7, size: 36 },
  { top: "58%", left: "84%", delay: 1.4, size: 52 },
  { top: "69%", left: "92%", delay: 0.3, size: 34 },
  { top: "80%", left: "84%", delay: 2.1, size: 44 },
  { top: "91%", left: "92%", delay: 1.0, size: 38 },
];

// ─── Single Flavor Box ─────────────────────────────────────────
function SingleFlavorBox() {
  const { dispatch, reserved, inventoryBase, inventoryLoaded } = useCart();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preselected = params.get("flavor");
  const [selectedFlavor, setSelectedFlavor] = useState(
    WEEKLY_FLAVORS.find((f) => f.id === preselected) ?? WEEKLY_FLAVORS[0]
  );
  const [selectedQty, setSelectedQty] = useState(QTY_OPTIONS[0]);
  const [added, setAdded] = useState(false);
  const [localError, setLocalError] = useState("");

  const remaining = inventoryBase[selectedFlavor.id] ?? 0;
  const reservedForFlavor = reserved[selectedFlavor.id] ?? 0;
  const halfDozensNeeded = selectedQty.count / 6;
  const canAdd = remaining - reservedForFlavor >= halfDozensNeeded;
  const soldOut = inventoryLoaded && remaining === 0;
  const discount = PROMO_ACTIVE ? (LAUNCH_DISCOUNTS[selectedQty.count] ?? 0) : 0;
  const discountedPrice = selectedQty.price - discount;

  const handleAdd = () => {
    if (soldOut || !canAdd) {
      setLocalError("Not enough inventory for that quantity.");
      setTimeout(() => setLocalError(""), 2500);
      return;
    }
    dispatch({
      type: "ADD_ITEM",
      item: {
        id: `${selectedFlavor.id}-${selectedQty.count}`,
        flavorId: selectedFlavor.id,
        name: `${selectedFlavor.name} — ${selectedQty.label}`,
        emoji: null,
        count: selectedQty.count,
        price: discountedPrice,
        category: "Single Flavor",
      },
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className={`card p-6 transition-shadow ${soldOut ? "opacity-60" : "hover:shadow-md"}`}>
      {PROMO_ACTIVE && (
        <div className="flex items-center gap-2 bg-sage-50 border border-sage-200 rounded-lg px-3 py-2 mb-5 text-xs font-bold text-sage-800">
          <Sparkles size={13} />
          Launch Special — Save $2–$4 depending on box size! Half-dozen minimum.
        </div>
      )}
      <div className="flex items-center gap-4 mb-5">
        <img src={selectedFlavor.image} alt={selectedFlavor.name} className="w-20 h-20 object-contain flex-shrink-0 drop-shadow-sm" />
        <div>
          <h3 className="font-display text-xl text-sage-800">Single Flavor Box</h3>
          <p className="text-sm text-gray-500">One flavor, your choice.</p>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-bold text-gray-500 block mb-2">Choose Flavor</label>
          <div className="flex flex-wrap gap-2">
            {WEEKLY_FLAVORS.map((f) => {
              const flavorSoldOut = inventoryLoaded && (inventoryBase[f.id] ?? 0) === 0;
              return (
                <button
                  key={f.id}
                  onClick={() => {
                    setSelectedFlavor(f);
                    setLocalError("");
                  }}
                  disabled={flavorSoldOut}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition-all ${
                    flavorSoldOut
                      ? "border-cream-200 text-gray-300 cursor-not-allowed opacity-50"
                      : selectedFlavor.id === f.id
                      ? "border-sage-500 bg-sage-50 text-sage-800"
                      : "border-cream-300 text-gray-500 hover:border-sage-300"
                  }`}
                >
                  <img src={f.image} alt={f.name} className="w-6 h-6 object-contain" />
                  {f.name}
                  {flavorSoldOut && <span className="text-xs text-red-400 font-bold ml-1">Sold Out</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-bold text-gray-500 block mb-1">Quantity</label>
            <select
              value={selectedQty.count}
              onChange={(e) => {
                const opt = QTY_OPTIONS.find((o) => o.count === parseInt(e.target.value));
                setSelectedQty(opt);
                setLocalError("");
              }}
              disabled={soldOut}
              className="w-full border border-cream-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-sage-300 disabled:opacity-50"
            >
              {QTY_OPTIONS.map((opt) => (
                <option key={opt.count} value={opt.count}>
                  {opt.label} — ${(opt.price - (PROMO_ACTIVE ? (LAUNCH_DISCOUNTS[opt.count] ?? 0) : 0)).toFixed(2)}
                </option>
              ))}
            </select>
            <div className="flex items-baseline gap-2 mt-2">
              {discount > 0 ? (
                <>
                  <span className="text-lg font-bold text-sage-700">${discountedPrice.toFixed(2)}</span>
                  <span className="text-sm text-gray-400 line-through">${selectedQty.price.toFixed(2)}</span>
                  <span className="text-xs text-amber-600 font-bold">Save ${discount.toFixed(2)}</span>
                </>
              ) : (
                <span className="text-lg font-bold text-sage-700">${selectedQty.price.toFixed(2)}</span>
              )}
            </div>
          </div>
          <div className="flex-shrink-0 pb-1">
            <button
              onClick={handleAdd}
              disabled={!inventoryLoaded || soldOut || !canAdd}
              className={`flex items-center justify-center gap-1.5 px-5 py-2 rounded-full text-sm font-bold transition-all ${
                !inventoryLoaded || soldOut || !canAdd
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : added
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : "bg-sage-600 hover:bg-sage-700 text-white"
              }`}
            >
              {!inventoryLoaded ? "Loading..." : soldOut ? "Sold Out" : added ? <><Check size={14} /> Added!</> : <><ShoppingBag size={14} /> Add to Cart</>}
            </button>
          </div>
        </div>
      </div>
      {localError && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
          <AlertCircle size={11} /> {localError}
        </p>
      )}
      {inventoryLoaded && !soldOut && !canAdd && !localError && (
        <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
          <AlertCircle size={11} /> Not enough inventory for that quantity — try a smaller box size.
        </p>
      )}
    </div>
  );
}

// ─── Assorted Box ──────────────────────────────────────────────
const PRICE_PER_HD = 15;
const BOX_MIN = 6;

function AssortedBox() {
  const { dispatch, reserved, inventoryBase, inventoryLoaded } = useCart();
  // quantities: macarons per flavor (not half-dozens)
  const [quantities, setQuantities] = useState(
    Object.fromEntries(WEEKLY_FLAVORS.map((f) => [f.id, 0]))
  );
  const [added, setAdded] = useState(false);
  const [localError, setLocalError] = useState("");

  const totalMacarons = Object.values(quantities).reduce((s, n) => s + n, 0);
  const totalPrice = (totalMacarons / 6) * PRICE_PER_HD;
  const meetsMinimum = totalMacarons >= BOX_MIN;

  const canAdd = meetsMinimum && WEEKLY_FLAVORS.every((f) => {
    if (quantities[f.id] === 0) return true;
    const maxForFlavor = Math.max(0, ((inventoryBase[f.id] ?? 0) - (reserved[f.id] ?? 0)) * 6);
    return quantities[f.id] <= maxForFlavor;
  });

  const handleAdd = () => {
    if (!meetsMinimum) return;
    if (!canAdd) {
      setLocalError("Not enough inventory for the selected quantities.");
      setTimeout(() => setLocalError(""), 2500);
      return;
    }
    WEEKLY_FLAVORS.forEach((f) => {
      if (quantities[f.id] === 0) return;
      const count = quantities[f.id];
      dispatch({
        type: "ADD_ITEM",
        item: {
          id: `assorted-${f.id}-${count}`,
          flavorId: f.id,
          name: `Assorted — ${f.name} (${count})`,
          emoji: null,
          count,
          price: (count / 6) * PRICE_PER_HD,
          category: "Assorted",
        },
      });
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      {PROMO_ACTIVE && (
        <div className="flex items-center gap-2 bg-sage-50 border border-sage-200 rounded-lg px-3 py-2 mb-5 text-xs font-bold text-sage-800">
          <Sparkles size={13} />
          Launch Special — Save $2–$4 depending on box size! Half-dozen minimum.
        </div>
      )}
      <div className="flex items-center gap-4 mb-5">
        <div className="flex -space-x-4 flex-shrink-0">
          {WEEKLY_FLAVORS.map((f, i) => (
            <img key={f.id} src={f.image} alt={f.name} className="w-14 h-14 object-contain drop-shadow-sm" style={{ zIndex: WEEKLY_FLAVORS.length - i }} />
          ))}
        </div>
        <div>
          <h3 className="font-display text-xl text-sage-800">Assorted Flavor Box</h3>
          <p className="text-sm text-gray-500">Mix and match your flavors.</p>
        </div>
      </div>

      {/* Per-flavor quantity steppers */}
      <div className="space-y-2 mb-4">
        {WEEKLY_FLAVORS.map((f) => {
          const flavorSoldOut = inventoryLoaded && (inventoryBase[f.id] ?? 0) === 0;
          const maxForFlavor = Math.max(0, ((inventoryBase[f.id] ?? 0) - (reserved[f.id] ?? 0)) * 6);
          const qty = quantities[f.id];
          return (
            <div key={f.id} className={`flex items-center justify-between bg-white border border-cream-200 rounded-lg px-3 py-2 ${flavorSoldOut ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-2.5">
                <img src={f.image} alt={f.name} className="w-9 h-9 object-contain flex-shrink-0" />
                <div>
                  <p className="text-sm font-bold text-sage-800 leading-none">{f.name}</p>
                  {flavorSoldOut && <span className="text-xs text-red-400 font-bold">Sold Out</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setQuantities((prev) => ({ ...prev, [f.id]: Math.max(0, prev[f.id] - 1) }))}
                  disabled={qty === 0}
                  className="w-7 h-7 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 disabled:opacity-30 disabled:cursor-not-allowed font-bold text-sm"
                >−</button>
                <span className="w-8 text-center text-sm font-bold text-sage-800">{qty}</span>
                <button
                  onClick={() => setQuantities((prev) => ({ ...prev, [f.id]: Math.min(maxForFlavor, prev[f.id] + 1) }))}
                  disabled={flavorSoldOut || qty >= maxForFlavor}
                  className="w-7 h-7 rounded-full border border-cream-300 flex items-center justify-center text-sage-700 hover:bg-sage-50 disabled:opacity-30 disabled:cursor-not-allowed font-bold text-sm"
                >+</button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Running total */}
      {totalMacarons > 0 && (
        <div className="flex justify-between items-baseline bg-sage-50 border border-sage-200 rounded-lg px-3 py-2.5 mb-4 text-sm">
          <span className={`font-bold ${meetsMinimum ? "text-sage-700" : "text-amber-600"}`}>
            {totalMacarons} selected {!meetsMinimum && `· need ${BOX_MIN - totalMacarons} more`}
          </span>
          <span className="font-display text-lg text-sage-800">${totalPrice.toFixed(2)}</span>
        </div>
      )}

      <button
        onClick={handleAdd}
        disabled={!inventoryLoaded || !canAdd}
        className={`w-full flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
          !inventoryLoaded || !canAdd
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : added
            ? "bg-green-100 text-green-700 border border-green-200"
            : "bg-sage-600 hover:bg-sage-700 text-white"
        }`}
      >
        {!inventoryLoaded
          ? "Loading..."
          : !meetsMinimum
          ? `Half-dozen minimum, add more!`
          : added
          ? <><Check size={14} /> Added!</>
          : <><ShoppingBag size={14} /> Add to Cart</>
        }
      </button>

      {localError && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
          <AlertCircle size={11} /> {localError}
        </p>
      )}
    </div>
  );
}
// ─── Main OrderMenu page ───────────────────────────────────────
export default function OrderMenu() {
  return (
  <div className="pt-20">
    {ORDERS_CLOSED ? (
      <section className="relative text-center py-32 px-4 bg-gradient-to-b from-cream-100 to-cream-50 overflow-hidden">
        <FloatingMacarons positions={HERO_MACARONS} />
        <div className="relative max-w-sm mx-auto">
          
          <h3 className="font-display text-3xl text-[#2f502a] mb-3">
            We're on Holiday
          </h3>
          <p className="text-gray-500 text-large leading-relaxed">
            Orders are closed until <span className="font-handwrite text-xl text-sage-500">{ORDERS_RETURN_DATE}.</span>
            <br/>
            Check back soon!
          </p>
        </div>
      </section>
    ) : (
      <>
        <section className="relative text-center py-16 px-4 bg-gradient-to-b from-cream-100 to-cream-50 overflow-hidden">
          <FloatingMacarons positions={HERO_MACARONS} />
          <p className="font-handwrite text-sage-500 text-xl mb-2">Ready to order?</p>
          <h1 className="font-display text-4xl text-sage-900 mb-3">Choose Your Box</h1>
          <p className="text-gray-500 text-sm max-w-sm mx-auto leading-relaxed">
            All macarons are 100% gluten free, made fresh to order.
          </p>
        </section>

        <section className="relative bg-cream-50 pt-4 pb-16 px-4 overflow-hidden">
          <FloatingMacarons positions={CONTENT_MACARONS} />
          <div className="relative max-w-2xl mx-auto space-y-8">

            <div className="flex items-start gap-3 rounded-xl p-4 text-sm leading-relaxed border bg-sage-50 border-sage-200 text-sage-800">
              <Calendar size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                <strong>Notice:</strong> All orders are fulfilled on a Saturday or Sunday - choose your pickup date/time at checkout.
                <span className="block mt-1 text-xs opacity-75">
                  Orders placed after Thursday at 5:00 PM CT are fulfilled the following weekend, at earliest.
                </span>
              </div>
            </div>

            <div className="space-y-4">
              <SingleFlavorBox />
              <AssortedBox />
            </div>

            <div className="bg-cream-100 rounded-xl p-4 text-xs text-gray-500 leading-relaxed">
              <strong className="text-sage-700">Allergen Notice:</strong> All products are gluten free
              and made in a dedicated gluten-free kitchen. Products contain eggs, dairy, and tree nuts (almond flour).
            </div>

          </div>
        </section>
      </>
    )}
  </div>
)}